import { type ZodType, type ZodTypeDef, z } from 'zod'
import { requestExplorerJson } from '../request'

export type BlockscoutClient = {
  get: <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
  >(parameters: {
    path: string
    query: Record<string, string>
    resultSchema: ZodType<Output, Def, Input>
    headers?: Record<string, string>
  }) => Promise<Output>
}

type CreateBlockscoutClientParameters = {
  apiBaseUrl: string
  proxyUrl?: string
}

export const createBlockscoutClient = ({
  apiBaseUrl,
  proxyUrl,
}: CreateBlockscoutClientParameters): BlockscoutClient => {
  const get = async <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
  >({
    path,
    query,
    resultSchema,
    headers,
  }: {
    path: string
    query: Record<string, string>
    resultSchema: ZodType<Output, Def, Input>
    headers?: Record<string, string>
  }): Promise<Output> => {
    // define the schema for the response
    const responseSchema = z.object({
      status: z.string(),
      message: z.string(),
      result: z.unknown(),
    })

    // request
    const url = new URL(path, apiBaseUrl)
    for (const [key, value] of Object.entries(query)) {
      url.searchParams.append(key, value)
    }
    console.debug(`[GET] ${url.toString()}`)
    const { status, message, result } = responseSchema.parse(
      await requestExplorerJson({
        url:
          proxyUrl === undefined
            ? url.toString()
            : `${proxyUrl}${encodeURIComponent(url.toString())}`,
        key: new URL(apiBaseUrl).origin,
        intervalMs: 500,
        rateGroup: 'blockscout',
        headers,
      }),
    )
    if (status !== '1') {
      throw typeof result === 'string' ? new Error(result) : new Error(message)
    }
    if (result === null || result === undefined) {
      throw new Error(message)
    }
    if (typeof result === 'string') {
      throw new TypeError(result)
    }
    return resultSchema.parse(result)
  }

  return { get }
}
